import { useQueryClient } from '@tanstack/react-query';
import { isTableServiceEnabled } from '@/lib/storefrontFulfillment';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useStorePageContext } from '@/layouts/storefrontContext';
import {
  createTableClaim,
  useDiningTables,
  useStorePage,
  type PublicDiningTable,
  type StorefrontError,
  type TableClaim,
} from '@/hooks/storefront/useStorefrontApi';

export interface StoredTableContext {
  version: 1;
  originTableId: string;
  serviceTableId: string;
  originSource: 'qr' | 'manual';
  qrToken: string | null;
}

interface StoredTableClaim extends TableClaim {
  qrToken: string;
}

const TABLE_CONTEXT_SYNC_EVENT = 'velko:store-table-context-sync';

export const resolveStoredTableContext = (
  diningTables: PublicDiningTable[],
  urlTable: string | null,
  stored: StoredTableContext | null,
): StoredTableContext | null => {
  const requested = urlTable
    ? diningTables.find((table) => table.public_token === urlTable || table.id === urlTable)
    : null;
  const validStoredOrigin = stored
    ? diningTables.some((table) => table.id === stored.originTableId)
    : false;
  const validStoredService = stored
    ? diningTables.some((table) => table.id === stored.serviceTableId)
    : false;

  if (requested) {
    const sameQrContext =
      stored?.version === 1 &&
      stored.originSource === 'qr' &&
      stored.qrToken === urlTable &&
      stored.originTableId === requested.id;
    return {
      version: 1,
      originTableId: requested.id,
      serviceTableId: sameQrContext && validStoredService ? stored.serviceTableId : requested.id,
      originSource: 'qr',
      qrToken: urlTable,
    };
  }
  if (stored?.version === 1 && validStoredOrigin) {
    return {
      ...stored,
      serviceTableId: validStoredService ? stored.serviceTableId : stored.originTableId,
    };
  }
  return null;
};

/**
 * Résout l'entrée storefront courante (slug boutique / slug page / jeton),
 * publie le contexte (branding + basePath) et gère la redirection permanente
 * des anciens slugs. Partagé par toutes les pages du storefront.
 */
export function useStoreEntry() {
  const queryClient = useQueryClient();
  const { shopSlug, pageSlug, accessToken } = useParams<{
    shopSlug?: string;
    pageSlug?: string;
    accessToken?: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { setContext, ...context } = useStorePageContext();
  // Incrémenté après unlock pour re-résoudre avec le grant.
  const [grantVersion, setGrantVersion] = useState(0);

  const searchParams = new URLSearchParams(location.search);
  const urlTable = searchParams.get('table');

  const [tableContext, setTableContext] = useState<StoredTableContext | null>(null);
  const [tableClaim, setTableClaim] = useState<StoredTableClaim | null>(null);
  const [tableClaimError, setTableClaimError] = useState<StorefrontError | null>(null);

  const prefix = location.pathname.startsWith('/shop') ? '/shop' : '';
  const basePath = useMemo(() => {
    if (accessToken) return `${prefix}/p/${accessToken}`;
    if (pageSlug) return `${prefix}/${shopSlug}/p/${pageSlug}`;
    return `${prefix}/${shopSlug}`;
  }, [prefix, shopSlug, pageSlug, accessToken]);

  const query = useStorePage({ shopSlug, pageSlug, accessToken }, grantVersion);
  const page = query.isError ? null : (query.data ?? null);
  useEffect(() => {
    if (!page?.id || page.locked) return;
    const search = new URLSearchParams(location.search);
    const utm: Record<string, string> = {};
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign']) {
      const value = search.get(key);
      if (value) utm[key] = value;
    }
    if (Object.keys(utm).length)
      sessionStorage.setItem(`commerce_utm_${page.id}`, JSON.stringify(utm));
  }, [page?.id, page?.locked, location.search]);
  const diningTablesQuery = useDiningTables(
    page && !page.locked && isTableServiceEnabled(page) ? page.id : undefined,
  );
  const diningTables = useMemo(() => diningTablesQuery.data ?? [], [diningTablesQuery.data]);
  const tableStorageKey = page ? `esaas_store_table_context_${page.id}` : null;
  const claimStorageKey = page ? `esaas_store_table_claim_${page.id}` : null;
  const presenceLevel =
    isTableServiceEnabled(page) && typeof page?.settings?.table_presence_level === 'string'
      ? (page.settings.table_presence_level as 'standard' | 'reinforced' | 'strict')
      : null;

  useEffect(() => {
    if (!tableStorageKey || diningTables.length === 0) return;
    if (presenceLevel) return;
    let stored: StoredTableContext | null = null;
    try {
      const value = sessionStorage.getItem(tableStorageKey);
      stored = value ? (JSON.parse(value) as StoredTableContext) : null;
    } catch {
      sessionStorage.removeItem(tableStorageKey);
    }

    const next = resolveStoredTableContext(diningTables, urlTable, stored);

    setTableContext(next);
    if (next) sessionStorage.setItem(tableStorageKey, JSON.stringify(next));
    else sessionStorage.removeItem(tableStorageKey);
  }, [diningTables, presenceLevel, tableStorageKey, urlTable]);

  const persistClaim = (next: StoredTableClaim) => {
    if (!claimStorageKey || !tableStorageKey) return;
    const origin = diningTables.find((table) => table.id === next.origin_table.id);
    if (!origin) return;
    let previousContext: StoredTableContext | null = null;
    try {
      const raw = sessionStorage.getItem(tableStorageKey);
      previousContext = raw ? (JSON.parse(raw) as StoredTableContext) : null;
    } catch {
      previousContext = null;
    }
    const retainedServiceTableId =
      previousContext?.originTableId === origin.id &&
      previousContext.qrToken === next.qrToken &&
      diningTables.some((table) => table.id === previousContext?.serviceTableId)
        ? previousContext.serviceTableId
        : origin.id;
    const contextValue: StoredTableContext = {
      version: 1,
      originTableId: origin.id,
      serviceTableId: retainedServiceTableId,
      originSource: 'qr',
      qrToken: next.qrToken,
    };
    setTableClaim(next);
    setTableContext(contextValue);
    setTableClaimError(null);
    sessionStorage.setItem(claimStorageKey, JSON.stringify(next));
    sessionStorage.setItem(tableStorageKey, JSON.stringify(contextValue));
    window.dispatchEvent(new Event(TABLE_CONTEXT_SYNC_EVENT));
  };

  useEffect(() => {
    if (!tableStorageKey) return;
    const synchronize = () => {
      try {
        const storedContext = sessionStorage.getItem(tableStorageKey);
        setTableContext(storedContext ? (JSON.parse(storedContext) as StoredTableContext) : null);
        if (claimStorageKey) {
          const storedClaim = sessionStorage.getItem(claimStorageKey);
          setTableClaim(storedClaim ? (JSON.parse(storedClaim) as StoredTableClaim) : null);
        }
      } catch {
        setTableContext(null);
        setTableClaim(null);
      }
    };
    window.addEventListener(TABLE_CONTEXT_SYNC_EVENT, synchronize);
    return () => window.removeEventListener(TABLE_CONTEXT_SYNC_EVENT, synchronize);
  }, [claimStorageKey, tableStorageKey]);

  useEffect(() => {
    if (
      !page ||
      !presenceLevel ||
      !claimStorageKey ||
      !tableStorageKey ||
      diningTables.length === 0
    )
      return;
    let cancelled = false;
    let stored: StoredTableClaim | null = null;
    try {
      const raw = sessionStorage.getItem(claimStorageKey);
      stored = raw ? (JSON.parse(raw) as StoredTableClaim) : null;
      if (stored && new Date(stored.expires_at).getTime() <= Date.now()) stored = null;
    } catch {
      sessionStorage.removeItem(claimStorageKey);
    }

    if (stored && (!urlTable || stored.qrToken === urlTable)) {
      persistClaim(stored);
      return;
    }
    if (!urlTable) {
      setTableClaim(null);
      setTableContext(null);
      return;
    }

    void createTableClaim(page.id, urlTable)
      .then((claim) => {
        if (!cancelled) persistClaim({ ...claim, qrToken: urlTable });
      })
      .catch((error: StorefrontError) => {
        if (!cancelled) {
          setTableClaimError(error);
          const table = (error.details?.table ?? null) as PublicDiningTable | null;
          if (table && diningTables.some((candidate) => candidate.id === table.id)) {
            setTableContext({
              version: 1,
              originTableId: table.id,
              serviceTableId: table.id,
              originSource: 'qr',
              qrToken: urlTable,
            });
          }
        }
      });

    return () => {
      cancelled = true;
    };
    // persistClaim intentionally uses the current table collection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimStorageKey, diningTables, page?.id, presenceLevel, tableStorageKey, urlTable]);

  const originTable =
    diningTables.find((table) => table.id === tableContext?.originTableId) ?? null;
  const serviceTable =
    diningTables.find((table) => table.id === tableContext?.serviceTableId) ?? null;

  const persistTableContext = (next: StoredTableContext | null) => {
    if (!tableStorageKey) return;
    setTableContext(next);
    if (next) {
      sessionStorage.setItem(tableStorageKey, JSON.stringify(next));
    } else {
      sessionStorage.removeItem(tableStorageKey);
    }
    window.dispatchEvent(new Event(TABLE_CONTEXT_SYNC_EVENT));
  };

  const updateOriginTable = (tableId: string | null) => {
    if (presenceLevel) return;
    const valid = tableId ? diningTables.find((table) => table.id === tableId) : null;
    persistTableContext(
      valid
        ? {
            version: 1,
            originTableId: valid.id,
            serviceTableId: valid.id,
            originSource: 'manual',
            qrToken: null,
          }
        : null,
    );
  };

  const submitTableSessionCode = async (code: string) => {
    if (!page || !urlTable || !presenceLevel) return false;
    try {
      const claim = await createTableClaim(page.id, urlTable, code);
      persistClaim({ ...claim, qrToken: urlTable });
      return true;
    } catch (error) {
      setTableClaimError(error as StorefrontError);
      return false;
    }
  };

  const updateServiceTable = (tableId: string | null) => {
    const valid = tableId ? diningTables.find((table) => table.id === tableId) : null;
    if (!valid) return;
    if (!tableContext || !originTable) {
      updateOriginTable(valid.id);
      return;
    }
    persistTableContext({ ...tableContext, serviceTableId: valid.id });
  };

  // Redirection permanente d'un ancien slug vers le lien courant.
  useEffect(() => {
    if (page?.redirect_to) {
      navigate(`${prefix}${page.redirect_to}`, { replace: true });
    }
  }, [page?.redirect_to, prefix, navigate]);

  // Publie le contexte layout (header/accent) dès la résolution.
  useEffect(() => {
    if (page && !page.redirect_to) {
      setContext({ page, basePath });
    } else if (query.isError) {
      setContext({ page: null, basePath: '' });
      const previousPageId = query.data?.id;
      if (previousPageId)
        queryClient.removeQueries({
          predicate: (cached) =>
            cached.queryKey[0] === 'storefront' &&
            cached.queryKey[1] !== 'page' &&
            cached.queryKey.includes(previousPageId),
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, basePath, query.isError]);

  return {
    ...query,
    page,
    basePath,
    diningTables,
    diningTablesLoading: diningTablesQuery.isLoading,
    originTable,
    serviceTable,
    activeTable: serviceTable,
    tableIdentifier: serviceTable?.name ?? null,
    tableContextSource: tableContext?.originSource ?? null,
    hasQrTableOrigin: tableContext?.originSource === 'qr',
    tablePresenceLevel: presenceLevel,
    tableClaim: tableClaim?.claim ?? null,
    tableClaimNetworkTrusted: tableClaim?.network_trusted ?? false,
    tableClaimError,
    submitTableSessionCode,
    setOriginTableId: updateOriginTable,
    setServiceTableId: updateServiceTable,
    setActiveTableId: updateServiceTable,
    currency: context.currency,
    refreshAfterUnlock: () => setGrantVersion((v) => v + 1),
  };
}

export function useCurrencyFormatter(currency: string) {
  return useMemo(
    () =>
      new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency || 'XOF',
        maximumFractionDigits: 0,
      }),
    [currency],
  );
}
